/**
 * The console's words for the six agent types (release 3.1 fleet wave 2a).
 *
 * The controller's modules/edge/agent-types.ts is the source: one registry row
 * per agent, `agentType` the one discriminator, and per row what the console
 * may offer (`supportedCommands`, `pauseSupport`) and why a control is limited
 * (`controlNote`). This file only NAMES those things; it never decides them.
 * A row from a controller before wave 2a carries none of the fields, and every
 * helper here then answers what the console did before (all four commands, a
 * pause toggle), so an older controller is not made to look broken.
 *
 * Pure (no React), so the table, the drawer, the rollout controls and the
 * tests share it.
 */
import {
  EDGE_AGENT_TYPES,
  type EdgeAgentClass,
  type EdgeAgentType,
  type EdgeCommandName,
  type EdgePauseSupport,
} from './api';
import { EDGE_AGENT_CLASS_LABEL } from './edge-health';

export { EDGE_AGENT_TYPES };

/** Human names (the controller's AGENT_TYPE_LABELS, capitalised for a table cell). */
export const EDGE_AGENT_TYPE_LABEL: Record<EdgeAgentType, string> = {
  laptop_agent: 'Laptop agent',
  server_scanner: 'Server scanner',
  dam_collector: 'DAM collector',
  gateway: 'Decision gateway',
  dsar_connector: 'DSAR actuation connector',
  agentless_collector: 'Agentless collection',
};

/** Plural, lower case, for sentences ("pause every DAM collector"). */
export const EDGE_AGENT_TYPE_PLURAL: Record<EdgeAgentType, string> = {
  laptop_agent: 'laptop agents',
  server_scanner: 'server scanners',
  dam_collector: 'DAM collectors',
  gateway: 'decision gateways',
  dsar_connector: 'DSAR actuation connectors',
  agentless_collector: 'agentless collection runs',
};

/**
 * A label as it reads inside a sentence: "Decision gateway" becomes "decision
 * gateway", but an acronym stays ("DAM collector", "DSAR actuation connector").
 */
export function inSentence(label: string): string {
  if (label.length > 1 && label[1] === label[1]?.toUpperCase() && label[1] !== label[1]?.toLowerCase()) return label;
  return label.charAt(0).toLowerCase() + label.slice(1);
}

export function isEdgeAgentType(v: unknown): v is EdgeAgentType {
  return typeof v === 'string' && (EDGE_AGENT_TYPES as readonly string[]).includes(v);
}

/**
 * The name of a row's agent type. A row without one (a controller before wave
 * 2a) falls back to its class; a type this build does not know is shown as
 * sent, never blank.
 */
export function edgeAgentTypeLabel(row: { agentType?: string | null; agentClass?: EdgeAgentClass | string | null }): string {
  if (isEdgeAgentType(row.agentType)) return EDGE_AGENT_TYPE_LABEL[row.agentType];
  if (typeof row.agentType === 'string' && row.agentType) return row.agentType.replace(/_/g, ' ');
  const cls = row.agentClass;
  if (typeof cls === 'string' && Object.hasOwn(EDGE_AGENT_CLASS_LABEL, cls)) {
    return EDGE_AGENT_CLASS_LABEL[cls as EdgeAgentClass];
  }
  return typeof cls === 'string' && cls ? cls : 'unknown';
}

/** Every command the console can queue, in the order the select lists them. */
export const EDGE_COMMANDS: readonly EdgeCommandName[] = ['pause', 'resume', 'collect_diagnostics', 'rescan'];

export const EDGE_COMMAND_LABEL: Record<EdgeCommandName, string> = {
  pause: 'Pause scanning',
  resume: 'Resume scanning',
  collect_diagnostics: 'Collect diagnostics',
  rescan: 'Rescan now',
};

/** For a DAM collector the same two commands stop and start collection, not scanning. */
const DAM_COMMAND_LABEL: Partial<Record<EdgeCommandName, string>> = {
  pause: 'Pause collection',
  resume: 'Resume collection',
};

export function edgeCommandLabel(command: EdgeCommandName, agentType?: string | null): string {
  if (agentType === 'dam_collector' && DAM_COMMAND_LABEL[command]) return DAM_COMMAND_LABEL[command] as string;
  return EDGE_COMMAND_LABEL[command];
}

/**
 * The commands to offer: the ones the device executes, in the console's
 * order. A row without `supportedCommands` (older controller) offers all four,
 * as before; a command this build does not know is dropped.
 */
export function offeredCommands(supported: readonly string[] | null | undefined): EdgeCommandName[] {
  if (!Array.isArray(supported)) return [...EDGE_COMMANDS];
  return EDGE_COMMANDS.filter((c) => supported.includes(c));
}

/**
 * Whether the drawer offers the pause toggle. Hidden only when the controller
 * says nothing would enforce it; an older controller (no field) keeps it.
 */
export function pauseOffered(pauseSupport: EdgePauseSupport | null | undefined): boolean {
  return pauseSupport !== 'none';
}

/** Who makes the pause real, in one line. */
export function pauseEnforcerText(pauseSupport: EdgePauseSupport | null | undefined): string {
  switch (pauseSupport) {
    case 'agent':
      return 'Enforced by the agent: it reads the pause on its next check-in and reports it back.';
    case 'controller':
      return (
        'Enforced by the controller: from the next call this device makes to the controller, ' +
        'it is refused while paused. There is nothing to deliver and nothing for the device to confirm.'
      );
    case 'none':
      return 'Nothing would enforce a pause of this device, so none is offered.';
    default:
      return 'Enforced by the agent on its next check-in.';
  }
}

/** The noun the pause stops, for the toggle's label ("Pause scanning", "Pause collection"). */
export function pauseNoun(agentType: string | null | undefined): string {
  switch (agentType) {
    case 'dam_collector':
    case 'agentless_collector':
      return 'collection';
    case 'gateway':
      return 'decision checks';
    case 'dsar_connector':
      return 'DSAR commands';
    default:
      return 'scanning';
  }
}

/**
 * A gateway's fail policy and what a pause does to customer traffic, from the
 * effectiveConfig it reports on its service heartbeat (`failPolicy`,
 * `onPause`, `paused`, `decisionTimeoutMs`). A gateway that reports none is an
 * older build: its default is closed, and the text says it was not reported
 * rather than guessing.
 */
export interface GatewayPausePolicy {
  failPolicy: 'closed' | 'degraded' | null;
  /** What a pause does to customer SQL through this gateway. */
  onPause: string;
  /** True when a pause would refuse customer SQL (closed). */
  refusesTraffic: boolean;
  pausedNow: boolean | null;
  decisionTimeoutMs: number | null;
}

export function gatewayPausePolicy(effectiveConfig: Record<string, unknown> | null | undefined): GatewayPausePolicy {
  const cfg = effectiveConfig ?? {};
  const raw = Object.hasOwn(cfg, 'failPolicy') ? cfg['failPolicy'] : undefined;
  const failPolicy = raw === 'closed' || raw === 'degraded' ? raw : null;
  const onPauseRaw = Object.hasOwn(cfg, 'onPause') ? cfg['onPause'] : undefined;
  const paused = Object.hasOwn(cfg, 'paused') ? cfg['paused'] : undefined;
  const timeout = Object.hasOwn(cfg, 'decisionTimeoutMs') ? cfg['decisionTimeoutMs'] : undefined;
  // `onPause` is derived from the fail policy by the gateway; the fail policy
  // is read first so the two can never be shown disagreeing.
  const mode: 'refuse' | 'allow' | 'unknown' =
    failPolicy === 'closed'
      ? 'refuse'
      : failPolicy === 'degraded'
        ? 'allow'
        : onPauseRaw === 'refuse_queries'
          ? 'refuse'
          : onPauseRaw === 'allow_queries_audited'
            ? 'allow'
            : 'unknown';
  const onPause =
    mode === 'refuse'
      ? 'While paused it refuses every customer query it would have checked (fail policy closed).'
      : mode === 'allow'
        ? 'While paused it lets customer queries through unchecked and records each one (fail policy degraded).'
        : 'While paused it applies its fail policy, which this gateway version does not report ' +
          '(closed, which refuses customer queries, unless its installer changed it).';
  return {
    failPolicy,
    onPause,
    // Unknown counts as refusing: closed is the default.
    refusesTraffic: mode !== 'allow',
    pausedNow: typeof paused === 'boolean' ? paused : null,
    decisionTimeoutMs: typeof timeout === 'number' && Number.isFinite(timeout) ? timeout : null,
  };
}

/** What a pause of every agent of one type does, for the rollout controls. */
export const AGENT_TYPE_PAUSE_EFFECT: Record<EdgeAgentType, string> = {
  laptop_agent: 'Every laptop agent stops scanning at its next check-in.',
  server_scanner: 'Every server scanner stops scanning at its next check-in.',
  dam_collector:
    'Every DAM collector from release 3.1 stops its readers at its next check-in and writes what it already read. ' +
    'A collector older than 3.1 does not read a pause and keeps collecting.',
  gateway:
    'Every decision gateway is refused by the controller on its next decision call (within about 2 s) and applies ' +
    'its fail policy: a gateway with fail policy closed REFUSES CUSTOMER SQL it would have checked for as long as ' +
    'the pause lasts; one with fail policy degraded lets queries through unchecked and records each one.',
  dsar_connector:
    'Every DSAR actuation connector is handed no new DSAR commands; a command already executing finishes and reports.',
  agentless_collector:
    'All agentless collection stops: new runs are refused and a running one stops before its next query.',
};
